import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { request } from "../api";

export const fetcher = async ({ queryKey, pageParam }) => {
  const [url, params] = queryKey;
  const response = await request.get(url, {
    params: { ...params, pageParam },
  });
  return response?.data;
};

export const useLoadMore = (url, params) => {
  const context = useInfiniteQuery(
    [url, params],
    ({ queryKey, pageParam = 1 }) => fetcher({ queryKey, pageParam }),
    {
      getPreviousPageParam: (firstPage) => firstPage.previousId ?? false,
      getNextPageParam: (lastPage) => {
        return lastPage.nextId ?? false;
      },
    }
  );

  return context;
};

export const usePrefetch = (url, params) => {
  const queryClient = useQueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0,
        cacheTime :5000
  
      },
    },
  });

  return () => {
    if (!url) {
      return;
    }

    queryClient.prefetchQuery([url, params], ({ queryKey }) =>
      fetcher({ queryKey })
    );
  };
};

export const useFetch = (url, params, config) => {
  const context = useQuery(
    [url, params],
    ({ queryKey }) => fetcher({ queryKey }),
    {
      enabled: !!url,
      ...config,
    }
  );

  return context;
};

const useGenericMutation = (func, url, params, updater) => {
  const queryClient = useQueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0,
        cacheTime :5000
  
      },
    },
  });

  return useMutation(func, {
    onMutate: async (data) => {
      await queryClient.cancelQueries([url, params]);

      const previousData = queryClient.getQueryData([url, params]);

      queryClient.setQueryData([url, params], (oldData) => {
        return updater ? updater(oldData, data) : data;
      });

      return previousData;
    },
    onError: (err, _, context) => {
      queryClient.setQueryData([url, params], context);
    },
    onSettled: () => {
      queryClient.invalidateQueries([url, params]);
    },
  });
};

export const useDelete = (url, params, updater) => {
  return useGenericMutation(
    (id) => request.delete(`${url}/${id}`),
    url,
    params,
    updater
  );
};

export const usePost = (url, params, updater,config) => {
  return useGenericMutation(
    (data) => request.post(url, data, config),
    url,
    params,
    updater
  );
};

export const useUpdate = (url, params, updater) => {
  return useGenericMutation(
    (data) => request.patch(url, data),
    url,
    params,
    updater
  );
};
